import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faClock, 
  faCheckCircle, 
  faList, 
  faCopy, 
  faFileAlt, 
  faTimes,
  faExclamationTriangle,
  faMoneyBillWave,
  faCalendarAlt,
  faPaperclip,
  faBell,
  faUser,
  faBuilding
} from '@fortawesome/free-solid-svg-icons';
import { getReimbursements, markReimbursementAsPaid } from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';
import { toast } from 'react-toastify';
import io from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import './PayerDashboard.css';

const PayerDashboard = () => {
  const { user } = useAuth();
  const [reimbursements, setReimbursements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMinistry, setSelectedMinistry] = useState('');
  const [activeTab, setActiveTab] = useState('pending'); // pending, paid, all
  const [confirmModal, setConfirmModal] = useState({ show: false, reimbursement: null });
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [viewDocument, setViewDocument] = useState({ show: false, url: '', fileName: '' });
  const [paymentProof, setPaymentProof] = useState(null);
  const [uploading, setUploading] = useState(false);
  const socketRef = useRef(null);
  const audioRef = useRef(null);
  const originalTitleRef = useRef(document.title);

  useEffect(() => {
    loadReimbursements();
    setupSocketConnection();
    
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);
  
  // Update page title with pending count
  useEffect(() => {
    const pendingCount = reimbursements.filter(r => r.status === 'approved').length;
    if (pendingCount > 0) {
      document.title = `(${pendingCount}) Pagamentos Pendentes`;
    } else {
      document.title = originalTitleRef.current;
    }
  }, [reimbursements]);
  
  const setupSocketConnection = () => {
    const serverUrl = process.env.REACT_APP_API_URL || window.location.origin;
    socketRef.current = io(serverUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });
    
    socketRef.current.on('connect', () => {
      console.log('✓ Conectado ao servidor de notificações');
      if (user?.id) {
        socketRef.current.emit('join', user.id);
      }
    });
    
    socketRef.current.on('new_payment', (payment) => {
      console.log('✓ Nova notificação de pagamento recebida:', payment);
      
      // Play notification sound
      playNotificationSound();
      
      // Show toast notification
      showPaymentToast(payment);
      
      // Add to reimbursements list
      loadReimbursements();
      
      // Flash the title for attention
      flashTitle();
    });
    
    socketRef.current.on('disconnect', () => {
      console.log('✗ Desconectado do servidor de notificações');
    });
    
    socketRef.current.on('connect_error', (error) => {
      console.log('Erro de conexão Socket.io, usando polling como fallback');
    });
  };
  
  const playNotificationSound = () => {
    try {
      // Create a simple notification sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.log('Não foi possível reproduzir som de notificação');
    }
  };
  
  const showPaymentToast = (payment) => {
    const toastContent = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}>
          <FontAwesomeIcon icon={faBell} />
          <span>Novo Pagamento Pendente</span>
        </div>
        <div style={{ fontSize: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <FontAwesomeIcon icon={faUser} size="sm" />
            <span>{payment.requester}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            <FontAwesomeIcon icon={faMoneyBillWave} size="sm" />
            <span style={{ fontWeight: 'bold', color: '#2ecc71' }}>{formatCurrency(payment.amount)}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FontAwesomeIcon icon={faBuilding} size="sm" />
            <span>{payment.ministry}</span>
          </div>
        </div>
      </div>
    );
    
    toast.info(toastContent, {
      position: "top-right",
      autoClose: 10000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      icon: false
    });
  };
  
  const flashTitle = () => {
    let count = 0;
    const originalTitle = document.title;
    const interval = setInterval(() => {
      document.title = count % 2 === 0 ? '● NOVO PAGAMENTO!' : originalTitle;
      count++;
      if (count > 6) {
        clearInterval(interval);
        // Update title with current pending count
        const pendingCount = reimbursements.filter(r => r.status === 'approved').length;
        document.title = pendingCount > 0 ? `(${pendingCount + 1}) Pagamentos Pendentes` : originalTitleRef.current;
      }
    }, 1000);
  };

  const loadReimbursements = async () => {
    try {
      setLoading(true);
      const response = await getReimbursements();
      setReimbursements(response.data);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar reembolsos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPixKey = (pixKey) => {
    navigator.clipboard.writeText(pixKey);
    showNotification('Chave PIX copiada com sucesso!', 'success');
  };

  const handlePaymentConfirm = (reimbursement) => {
    setConfirmModal({ show: true, reimbursement });
    setPaymentProof(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        showNotification('Apenas arquivos PDF, JPG e PNG são permitidos', 'error');
        e.target.value = '';
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        showNotification('O arquivo deve ter no máximo 5MB', 'error');
        e.target.value = '';
        return;
      }
      
      setPaymentProof(file);
    }
  };

  const handlePaymentExecute = async () => {
    try {
      setUploading(true);
      const reimbursement = confirmModal.reimbursement;
      
      const formData = new FormData();
      if (paymentProof) {
        formData.append('paymentProof', paymentProof);
      }
      
      await markReimbursementAsPaid(reimbursement._id, formData);
      
      // Update the reimbursement status in the list instead of removing it
      setReimbursements(prev => prev.map(r => 
        r._id === reimbursement._id 
          ? { ...r, status: 'paid', paidAt: new Date(), paymentProofFileName: paymentProof?.name }
          : r
      ));
      
      setConfirmModal({ show: false, reimbursement: null });
      setPaymentProof(null);
      showNotification('Pagamento registrado com sucesso!', 'success');
      
      // Switch to paid tab to show the newly paid item
      setActiveTab('paid');
    } catch (err) {
      showNotification('Erro ao registrar pagamento', 'error');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const handleViewDocument = (reimbursement) => {
    setViewDocument({
      show: true,
      url: reimbursement.fileUrl,
      fileName: reimbursement.fileName
    });
  };

  const handleViewPaymentProof = (reimbursement) => {
    setViewDocument({
      show: true,
      url: reimbursement.paymentProofUrl,
      fileName: reimbursement.paymentProofFileName
    });
  };

  const getMinistries = () => {
    const ministries = [...new Set(reimbursements.map(r => r.ministry?.name))].filter(Boolean);
    return ministries.sort();
  };

  // Filter by tab
  const getFilteredByTab = () => {
    if (activeTab === 'pending') {
      return reimbursements.filter(r => r.status === 'approved');
    } else if (activeTab === 'paid') {
      return reimbursements.filter(r => r.status === 'paid');
    }
    return reimbursements; // all
  };

  // Filter by search and ministry
  const filteredReimbursements = getFilteredByTab().filter(r => {
    const matchesSearch = 
      r.requester.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.requester.cpfCnpj.includes(searchTerm) ||
      r.pixKey.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatCurrency(r.amount).includes(searchTerm);
    
    const matchesMinistry = !selectedMinistry || r.ministry?.name === selectedMinistry;
    
    return matchesSearch && matchesMinistry;
  });

  const pendingReimbursements = reimbursements.filter(r => r.status === 'approved');
  const paidReimbursements = reimbursements.filter(r => r.status === 'paid');
  
  const totalPending = pendingReimbursements.reduce((sum, r) => sum + r.amount, 0);
  const totalPaid = paidReimbursements.reduce((sum, r) => sum + r.amount, 0);

  if (loading) {
    return <div className="payer-dashboard"><div className="loading">Carregando...</div></div>;
  }

  return (
    <div className="payer-dashboard">
      <div className="payer-header">
        <h1>Gestão de Pagamentos</h1>
        <div className="payer-stats">
          <div className="stat-card pending">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faClock} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{pendingReimbursements.length}</div>
              <div className="stat-label">Pendentes</div>
              <div className="stat-amount">{formatCurrency(totalPending)}</div>
            </div>
          </div>
          <div className="stat-card paid">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{paidReimbursements.length}</div>
              <div className="stat-label">Pagos</div>
              <div className="stat-amount">{formatCurrency(totalPaid)}</div>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Tabs */}
      <div className="tabs-container">
        <button
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
          title="Reembolsos aprovados que aguardam o seu pagamento"
        >
          <FontAwesomeIcon icon={faClock} />
          <span>Pendentes ({pendingReimbursements.length})</span>
        </button>
        <button
          className={`tab ${activeTab === 'paid' ? 'active' : ''}`}
          onClick={() => setActiveTab('paid')}
          title="Histórico de reembolsos já pagos"
        >
          <FontAwesomeIcon icon={faCheckCircle} />
          <span>Pagos ({paidReimbursements.length})</span>
        </button>
        <button
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
          title="Todos os reembolsos, independente do status"
        >
          <FontAwesomeIcon icon={faList} />
          <span>Todos ({reimbursements.length})</span>
        </button>
      </div>

      <div className="payer-filters">
        <input
          type="text"
          className="search-input"
          placeholder="Buscar por nome, CPF/CNPJ, PIX ou valor..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="filter-select"
          value={selectedMinistry}
          onChange={(e) => setSelectedMinistry(e.target.value)}
        >
          <option value="">Todos os Ministérios</option>
          {getMinistries().map(ministry => (
            <option key={ministry} value={ministry}>{ministry}</option>
          ))}
        </select>
      </div>

      {filteredReimbursements.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <FontAwesomeIcon icon={activeTab === 'pending' ? faClock : faCheckCircle} />
          </div>
          <h3>
            {activeTab === 'pending' && 'Nenhum pagamento pendente'}
            {activeTab === 'paid' && 'Nenhum pagamento realizado'}
            {activeTab === 'all' && 'Nenhum reembolso encontrado'}
          </h3>
          <p>
            {activeTab === 'pending' && 'Todos os reembolsos aprovados foram pagos!'}
            {activeTab === 'paid' && 'Você ainda não realizou nenhum pagamento.'}
            {activeTab === 'all' && 'Não há reembolsos no sistema.'}
          </p>
        </div>
      ) : (
        <div className="reimbursements-grid">
          {filteredReimbursements.map(reimbursement => (
            <div 
              key={reimbursement._id} 
              className={`reimbursement-card ${reimbursement.status === 'paid' ? 'paid-card' : 'pending-card'}`}
            >
              <div className="card-header">
                <div className="ministry-badge">{reimbursement.ministry?.name}</div>
                <div className="card-date">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                  {formatDate(reimbursement.createdAt)}
                </div>
              </div>
              
              <div className="card-status-badge">
                {reimbursement.status === 'paid' ? (
                  <span className="status-badge paid-badge">
                    <FontAwesomeIcon icon={faCheckCircle} /> Pago
                  </span>
                ) : (
                  <span className="status-badge pending-badge">
                    <FontAwesomeIcon icon={faClock} /> Pendente
                  </span>
                )}
              </div>
              
              <div className="card-body">
                <div className="requester-info">
                  <h3>{reimbursement.requester.name}</h3>
                  <p className="cpf-cnpj">{reimbursement.requester.cpfCnpj}</p>
                </div>

                <div className="amount-display">
                  <FontAwesomeIcon icon={faMoneyBillWave} />
                  {formatCurrency(reimbursement.amount)}
                </div>

                <div className="description">
                  <strong>Descrição:</strong> {reimbursement.description}
                </div>

                {reimbursement.status === 'approved' && (
                  <div className="pix-key-container">
                    <label>Chave PIX:</label>
                    <div className="pix-key-display">
                      <span className="pix-key">{reimbursement.pixKey}</span>
                      <button
                        className="btn-copy"
                        onClick={() => handleCopyPixKey(reimbursement.pixKey)}
                        title="Copiar chave PIX"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                    </div>
                  </div>
                )}

                {reimbursement.status === 'paid' && (
                  <div className="payment-info">
                    <div className="info-row">
                      <FontAwesomeIcon icon={faCheckCircle} className="info-icon" />
                      <span>Pago em: {formatDate(reimbursement.paidAt)}</span>
                    </div>
                    {reimbursement.paidBy && (
                      <div className="info-row">
                        <span className="info-label">Por:</span>
                        <span>{reimbursement.paidBy.username || reimbursement.paidBy.name}</span>
                      </div>
                    )}
                    {reimbursement.paymentProofFileName && (
                      <div className="info-row">
                        <FontAwesomeIcon icon={faPaperclip} className="info-icon" />
                        <span>Comprovante anexado</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="approved-info">
                  <small>
                    Aprovado por {reimbursement.approvedBy?.username} em{' '}
                    {formatDate(reimbursement.approvedAt)}
                  </small>
                </div>
              </div>

              <div className="card-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => handleViewDocument(reimbursement)}
                >
                  <FontAwesomeIcon icon={faFileAlt} />
                  Ver Comprovante
                </button>
                {reimbursement.status === 'approved' ? (
                  <button
                    className="btn btn-primary"
                    onClick={() => handlePaymentConfirm(reimbursement)}
                  >
                    <FontAwesomeIcon icon={faCheckCircle} />
                    Marcar como Pago
                  </button>
                ) : (
                  reimbursement.paymentProofUrl && (
                    <button
                      className="btn btn-info"
                      onClick={() => handleViewPaymentProof(reimbursement)}
                    >
                      <FontAwesomeIcon icon={faPaperclip} />
                      Ver Comprovante de Pagamento
                    </button>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className="modal-overlay" onClick={() => setConfirmModal({ show: false, reimbursement: null })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Confirmar Pagamento</h2>
            <div className="modal-body">
              <p>Você confirma que realizou o pagamento de:</p>
              <div className="confirm-details">
                <strong>{confirmModal.reimbursement?.requester.name}</strong>
                <div className="confirm-amount">
                  <FontAwesomeIcon icon={faMoneyBillWave} />
                  {formatCurrency(confirmModal.reimbursement?.amount)}
                </div>
                <p>Chave PIX: {confirmModal.reimbursement?.pixKey}</p>
              </div>
              
              <div className="payment-proof-section">
                <label className="payment-proof-label">
                  <FontAwesomeIcon icon={faPaperclip} /> Comprovante de Pagamento (Opcional)
                </label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="file-input"
                />
                {paymentProof && (
                  <div className="file-selected">
                    <span>
                      <FontAwesomeIcon icon={faFileAlt} /> {paymentProof.name}
                    </span>
                    <button
                      className="btn-remove-file"
                      onClick={() => setPaymentProof(null)}
                      type="button"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                )}
                <small className="file-help">
                  Aceita PDF, JPG ou PNG (máx. 5MB)
                </small>
              </div>

              <p className="warning-text">
                <FontAwesomeIcon icon={faExclamationTriangle} /> Esta ação não pode ser desfeita. O reembolso será marcado como pago.
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setConfirmModal({ show: false, reimbursement: null })}
                disabled={uploading}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handlePaymentExecute}
                disabled={uploading}
              >
                {uploading ? 'Processando...' : 'Confirmar Pagamento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {viewDocument.show && (
        <div className="modal-overlay" onClick={() => setViewDocument({ show: false, url: '', fileName: '' })}>
          <div className="modal-content document-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Comprovante</h2>
              <button
                className="btn-close"
                onClick={() => setViewDocument({ show: false, url: '', fileName: '' })}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <p className="document-name">
                <FontAwesomeIcon icon={faFileAlt} /> {viewDocument.fileName}
              </p>
              <a
                href={viewDocument.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Abrir Documento em Nova Aba
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default PayerDashboard;
